import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../Common/hooks/useToast";
import { sendLoginOtp, verifyLoginOtp } from "../https/login_module.https";
import { LoginTab, LoginModuleHookReturn } from "../interfaces/login_module.interface";

export const useLoginModule = (): LoginModuleHookReturn => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [activeTab, setActiveTabState] = useState<LoginTab>("phone");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
  const [timer, setTimer] = useState(45);
  const [isSending, setIsSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const otpInputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!otpSent || timer <= 0) return;
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [otpSent, timer]);

  const resetOtpForm = () => {
    setErrorMsg("");
    setOtpSent(false);
    setOtp(Array(6).fill(""));
    setTimer(45);
  };

  const setActiveTab = (tab: LoginTab) => {
    setActiveTabState(tab);
    resetOtpForm();
  };

  const handleSendOtp = async () => {
    if (activeTab === "phone") {
      if (phone.length !== 10) {
        setErrorMsg("Please enter a valid 10-digit phone number");
        return;
      }
    } else {
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setErrorMsg("Please enter a valid email address");
        return;
      }
    }

    setErrorMsg("");
    setIsSending(true);
    try {
      await sendLoginOtp(activeTab, phone, email);
      setOtpSent(true);
      setTimer(45);
      showToast("OTP sent successfully", "success");
    } catch (error: any) {
      setErrorMsg(error.response?.data?.message || "Failed to send OTP. Please check the credentials.");
    } finally {
      setIsSending(false);
    }
  };

  const handleVerifyOtp = async () => {
    const fullOtp = otp.join("");
    if (fullOtp.length !== 6) {
      setErrorMsg("Please enter the complete 6-digit OTP");
      return;
    }

    setErrorMsg("");
    setIsSending(true);
    try {
      const response = await verifyLoginOtp(activeTab, phone, email, fullOtp);
      const data = response.data;
      if (data?.userId) {
        localStorage.setItem("doctorId", data.userId);
        localStorage.setItem("doctorName", data.name || "Doctor");
        localStorage.setItem("doctorRole", "DOCTOR");
        showToast("Logged in successfully!", "success");
        navigate("/dashboard");
      }
    } catch (error: any) {
      setErrorMsg(error.response?.data?.message || "Invalid OTP. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleOtpChange = (value: string, index: number) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split("");
      setOtp(newOtp);
      otpInputsRef.current[5]?.focus();
    }
  };

  return {
    activeTab,
    phone,
    email,
    otpSent,
    otp,
    timer,
    isSending,
    errorMsg,
    otpInputsRef,
    setActiveTab,
    setPhone,
    setEmail,
    handleSendOtp,
    handleVerifyOtp,
    handleOtpChange,
    handleOtpKeyDown,
    handleOtpPaste,
    resetOtpForm,
  };
};
